import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'

interface Props {
  /** Why approval is blocked, or null when it is allowed. Rendered next to the button. */
  blockReason: string | null
  isSubmitting: boolean
  primaryLabel: string
  secondaryLabel: string
  /** False without the approve permission — the button (and its block reason) is not rendered. */
  isApproveVisible: boolean
  /** False without the recollect / reject permission — the button is not rendered. */
  isSecondaryVisible: boolean
  onApprove(): void
  onSecondary(): void
  onSkip(): void
}

/**
 * A disabled Approve always says why. An unexplained dead button is what made the Retool screen
 * hard to work — a reviewer could not tell a missing date from a broken page.
 *
 * Approve and the secondary decision are hidden (not disabled) when the reviewer lacks their
 * permission (ENG-1207 part 2). "Skip for now" sends no request and always stays.
 */
const ReviewDecisionBarComponent = ({
  blockReason,
  isSubmitting,
  primaryLabel,
  secondaryLabel,
  isApproveVisible,
  isSecondaryVisible,
  onApprove,
  onSecondary,
  onSkip,
}: Props) => {
  return (
    <div css={tw`flex flex-col gap-2`}>
      {isApproveVisible && blockReason ? (
        <Typography variant="body2" css={tw`text-warning-500`} data-testid="review-block-reason">
          {blockReason}
        </Typography>
      ) : null}
      <div css={tw`flex flex-row items-center gap-2`}>
        {isApproveVisible ? (
          <Button
            size="small"
            onClick={onApprove}
            disabled={Boolean(blockReason) || isSubmitting}
            data-testid="review-approve-button"
          >
            {primaryLabel}
          </Button>
        ) : null}
        {isSecondaryVisible ? (
          <Button
            size="small"
            color="red"
            onClick={onSecondary}
            disabled={isSubmitting}
            data-testid="review-secondary-button"
          >
            {secondaryLabel}
          </Button>
        ) : null}
        <Button
          size="small"
          color="white"
          onClick={onSkip}
          disabled={isSubmitting}
          data-testid="review-skip-button"
        >
          Skip for now
        </Button>
      </div>
    </div>
  )
}

export const ReviewDecisionBar = memo(ReviewDecisionBarComponent)
